import { notFound } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { isStayPropertyType } from "@majourneys/shared";
import { withApi404 } from "../../../lib/route-loader";
import { categoryBySlugQueryOptions, cityBySlugQueryOptions } from "../hooks/use-seo-lookup";
import { seoLookupApi } from "./seo-lookup.api";
import { isExploreSegmentSlug, isUnionHubSlug } from "../lib/explore-segments";

/**
 * Hub slugs that are real without a `categories` row: explore segments
 * (`locations`, `guides`, …), the union hubs, and the hotel property types,
 * whose places live in their own tables.
 */
export function isHubOnlyCategorySlug(slug: string): boolean {
  return isExploreSegmentSlug(slug) || isUnionHubSlug(slug) || isStayPropertyType(slug);
}

/**
 * Throws `notFound()` (a real HTTP 404) when a hub's city or category slug
 * does not exist. A real hub with nothing in it is not this: it stays a 200
 * with `noindex,follow`. A failing API is not this either; `withApi404`
 * retries it once and then lets the error page say so, rather than telling
 * Google a live page is gone.
 *
 * The page's own lookups (`useCityBySlug`, `useCategoryBySlug`) answer null
 * for any failure, which left /place/zzz-city and /category/zzz-nope as
 * 200 + noindex pages that Google files under "Soft 404". This asks the same
 * question under the same query key without swallowing the 404, so the row
 * fetched here is the one the page then reads.
 */
export async function ensureHubSlugsExist(
  queryClient: QueryClient,
  { city, category }: { city?: string; category?: string },
): Promise<void> {
  await Promise.all([
    city
      ? mustExist(queryClient, cityBySlugQueryOptions(city).queryKey, () => seoLookupApi.cityBySlug(city))
      : null,
    category && !isHubOnlyCategorySlug(category)
      ? mustExist(queryClient, categoryBySlugQueryOptions(category).queryKey, () =>
          seoLookupApi.categoryBySlug(category),
        )
      : null,
  ]);
}

async function mustExist(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  queryFn: () => Promise<unknown>,
): Promise<void> {
  // The lenient page lookup caches null for any failure under this key, so a
  // cached null is asked again rather than trusted.
  const row =
    (await withApi404(() => queryClient.ensureQueryData({ queryKey, queryFn }))) ??
    (await withApi404(() => queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 })));
  if (!row) throw notFound();
}
